import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, INR, formatApiError, downloadFile, fmtDMY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/Badges";
import DatePicker from "@/components/DatePicker";
import { Plus, Upload, Check, Trash2, FileText, X, Undo2, Download, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import BulkBar from "@/components/BulkBar";
import MarkPaidDialog from "@/components/invoices/MarkPaidDialog";
import DuplicateInvoiceDialog from "@/components/invoices/DuplicateInvoiceDialog";
import ImportCSVDialog from "@/components/ImportCSVDialog";
import DueByPill from "@/components/invoices/DueByPill";
import InvoiceFiltersBar from "@/components/invoices/InvoiceFiltersBar";
import InvoiceDetailDialog from "@/components/invoices/InvoiceDetailDialog";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";

const initial = {
  entity_type: "client",
  entity_id: "",
  invoice_number: "",
  invoice_date: new Date().toISOString().slice(0, 10),
  billing_period_start: "",
  billing_period_end: "",
  due_date: "",
  amount: "",
  notes: "",
  pdf_file_id: null,
  pdf_filename: null,
};

const INVOICE_IMPORT_PREVIEW_COLUMNS = [
  { key: "entity_name", label: "Entity" },
  { key: "entity_type", label: "Type" },
  { key: "invoice_number", label: "Invoice #" },
  { key: "amount", label: "Amount" },
  { key: "invoice_date", label: "Invoice date" },
  { key: "due_date", label: "Due" },
  { key: "status", label: "Status" },
];
const PAYMENT_IMPORT_PREVIEW_COLUMNS = [
  { key: "invoice_number", label: "Invoice #" },
  { key: "amount", label: "Amount" },
  { key: "new_status", label: "New status" },
  { key: "remaining", label: "Remaining" },
];

const INVOICE_IMPORT_COLUMNS = [
  "entity_type",
  "entity_name",
  "invoice_number",
  "invoice_date",
  "billing_period_start",
  "billing_period_end",
  "due_date (optional)",
  "amount",
  "payment_date (optional — fill to import as paid)",
  "utr_number (optional)",
  "payment_id (optional)",
  "notes",
];

const PAYMENT_IMPORT_COLUMNS = [
  "entity_type",
  "invoice_number",
  "amount",
  "payment_date",
  "utr_number (or payment_id)",
  "payment_id (or utr_number)",
  "notes",
];

export default function InvoicesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canSeeClient = isAdmin || user?.role === "client_team";
  const canSeePartner = isAdmin || user?.role === "partner_team";
  const defaultType = canSeeClient ? "client" : "partner";

  const [invoices, setInvoices] = useState([]);
  const [entities, setEntities] = useState({ client: [], partner: [] });
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    entity_type: searchParams.get("type") || "all",
    status: "all",
    month: "",
    q: "",
    entity_id: searchParams.get("entity") || "all",
    paid_in: "all", // all | week | month | 7d | 30d
  });

  // Consume the URL params once so they don't linger on the URL
  useEffect(() => {
    if (searchParams.get("entity") || searchParams.get("type")) {
      const next = new URLSearchParams(searchParams);
      next.delete("entity");
      next.delete("type");
      setSearchParams(next, { replace: true });
    }
  }, []);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...initial, entity_type: defaultType });
  const [uploading, setUploading] = useState(false);

  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ utr_number: "", payment_id: "", payment_date: new Date().toISOString().slice(0, 10), mode: "full", paid_amount: "" });

  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  // Sort state: null = use backend default (invoice_date desc). Otherwise { by, order }.
  const [sort, setSort] = useState(null);

  const cycleAmountSort = () => {
    setSort((prev) => {
      if (!prev || prev.by !== "amount") return { by: "amount", order: "desc" };
      if (prev.order === "desc") return { by: "amount", order: "asc" };
      return null;
    });
    setPage(1);
  };

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [dupOpen, setDupOpen] = useState(false);
  const [dupSource, setDupSource] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importPaymentsOpen, setImportPaymentsOpen] = useState(false);

  // Memoize sorted payments so we don't re-sort on every render
  const sortedDetailPayments = useMemo(() => {
    if (!detail?.payments || detail.payments.length === 0) return [];
    return detail.payments
      .slice()
      .sort((a, b) => (a.payment_date || "").localeCompare(b.payment_date || ""));
  }, [detail?.payments]);

  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === invoices.length) setSelected(new Set());
    else setSelected(new Set(invoices.map((x) => x.id)));
  };
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!window.confirm(`Delete ${ids.length} invoice${ids.length === 1 ? "" : "s"}?`)) return;
    try {
      const { data } = await api.post(`/invoices/bulk-delete`, { ids });
      toast.success(`Deleted ${data.deleted} invoice${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const bulkDuplicate = async () => {
    const ids = Array.from(selected);
    if (ids.length === 1) {
      // Single-select → open override dialog
      const src = invoices.find((x) => x.id === ids[0]);
      if (!src) return;
      setDupSource(src);
      setDupOpen(true);
      return;
    }
    if (!window.confirm(`Duplicate ${ids.length} invoice${ids.length === 1 ? "" : "s"}? Copies will be created as unpaid with today's date.`)) return;
    try {
      const { data } = await api.post(`/invoices/bulk-duplicate`, { ids });
      toast.success(`Duplicated ${data.created} invoice${data.created === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitDuplicateOverride = async (overrides) => {
    if (!dupSource) return;
    try {
      await api.post(`/invoices/${dupSource.id}/duplicate`, overrides);
      toast.success("Duplicated invoice");
      setDupOpen(false);
      setDupSource(null);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openDetail = async (inv) => {
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/invoices/${inv.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.entity_type !== "all") params.entity_type = filters.entity_type;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.month) params.month = filters.month;
      if (filters.q) params.q = filters.q;
      if (filters.entity_id !== "all") params.entity_id = filters.entity_id;
      Object.assign(params, paidRange());
      const res = await api.get("/invoices/export", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const parts = ["invoices"];
      if (filters.month) parts.push(filters.month);
      if (filters.entity_type !== "all") parts.push(filters.entity_type);
      if (filters.status !== "all") parts.push(filters.status);
      a.download = `${parts.join("_")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const loadEntities = useCallback(async () => {
    const next = { client: [], partner: [] };
    if (canSeeClient) {
      try {
        const { data } = await api.get(`/entities/client`);
        next.client = data;
      } catch {
        // Silently skip — the entity list may fail during transient outages;
        // callers must handle the missing list themselves.
      }
    }
    if (canSeePartner) {
      try {
        const { data } = await api.get(`/entities/partner`);
        next.partner = data;
      } catch {
        // Silently skip — see comment above.
      }
    }
    setEntities(next);
  }, [canSeeClient, canSeePartner]);

  const paidRange = useCallback(() => {
    const t = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (filters.paid_in === "week") {
      const monday = new Date(t);
      const day = monday.getDay() || 7; // Sunday = 0 → 7
      monday.setDate(monday.getDate() - (day - 1));
      return { paid_from: iso(monday), paid_to: iso(t) };
    }
    if (filters.paid_in === "month") {
      return { paid_from: `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-01`, paid_to: iso(t) };
    }
    if (filters.paid_in === "7d") {
      const from = new Date(t);
      from.setDate(t.getDate() - 6);
      return { paid_from: iso(from), paid_to: iso(t) };
    }
    if (filters.paid_in === "30d") {
      const from = new Date(t);
      from.setDate(t.getDate() - 29);
      return { paid_from: iso(from), paid_to: iso(t) };
    }
    return {};
  }, [filters.paid_in]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filters.entity_type !== "all") params.entity_type = filters.entity_type;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.month) params.month = filters.month;
      if (filters.q) params.q = filters.q;
      if (filters.entity_id !== "all") params.entity_id = filters.entity_id;
      if (sort) {
        params.sort_by = sort.by;
        params.sort_order = sort.order;
      }
      Object.assign(params, paidRange());
      const { data } = await api.get("/invoices", { params });
      setInvoices(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [filters, paidRange, sort]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);
  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    let out = 0,
      col = 0;
    invoices.forEach((i) => {
      if (i.status === "paid") col += i.amount;
      else out += i.amount;
    });
    return { out, col, count: invoices.length };
  }, [invoices]);

  const totalCount = invoices.length;
  const paginatedInvoices = useMemo(() => {
    if (pageSize === "All" || pageSize === 0) return invoices;
    const start = (page - 1) * pageSize;
    return invoices.slice(start, start + pageSize);
  }, [invoices, page, pageSize]);

  // Reset to first page whenever filters shrink/change the result set.
  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...initial, entity_type: defaultType });
    setOpen(true);
  };
  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      entity_type: inv.entity_type,
      entity_id: inv.entity_id,
      invoice_number: inv.invoice_number,
      invoice_date: inv.invoice_date,
      billing_period_start: inv.billing_period_start,
      billing_period_end: inv.billing_period_end,
      due_date: inv.due_date,
      amount: inv.amount,
      notes: inv.notes || "",
      pdf_file_id: inv.pdf_file_id || null,
      pdf_filename: inv.pdf_file_id ? "Attached PDF" : null,
    });
    setOpen(true);
  };

  const onPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, pdf_file_id: data.file_id, pdf_filename: data.original_filename }));
      toast.success("PDF uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.entity_id) return toast.error("Entity is required");
    if (!form.invoice_number.trim()) return toast.error("Invoice number is required");
    if (!form.invoice_date) return toast.error("Invoice date is required");
    if (!form.billing_period_start) return toast.error("Billing period start is required");
    if (!form.billing_period_end) return toast.error("Billing period end is required");
    if (!form.due_date) return toast.error("Due date is required");
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Amount must be greater than 0");
    const payload = {
      entity_type: form.entity_type,
      entity_id: form.entity_id,
      invoice_number: form.invoice_number.trim(),
      invoice_date: form.invoice_date,
      billing_period_start: form.billing_period_start,
      billing_period_end: form.billing_period_end,
      due_date: form.due_date,
      amount: parseFloat(form.amount),
      notes: form.notes || null,
      pdf_file_id: form.pdf_file_id || null,
    };
    try {
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? "Invoice updated" : "Invoice created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPay = (inv) => {
    setPayTarget(inv);
    setPayForm({ utr_number: "", payment_id: "", payment_date: new Date().toISOString().slice(0, 10), mode: "full", paid_amount: String(inv.remaining_amount || inv.amount) });
    setPayOpen(true);
  };
  const submitPay = async () => {
    if (!payForm.utr_number && !payForm.payment_id) return toast.error("UTR or Payment ID required");
    const body = {
      utr_number: payForm.utr_number || null,
      payment_id: payForm.payment_id || null,
      payment_date: payForm.payment_date,
    };
    if (payForm.mode === "partial") {
      const amt = parseFloat(payForm.paid_amount);
      if (!amt || amt <= 0) return toast.error("Enter a valid partial amount");
      body.paid_amount = amt;
    }
    try {
      await api.post(`/invoices/${payTarget.id}/mark-paid`, body);
      toast.success(payForm.mode === "partial" ? "Partial payment recorded" : "Invoice marked paid");
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markUnpaid = async (inv) => {
    if (!window.confirm("Revert this invoice to unpaid?")) return;
    try {
      await api.post(`/invoices/${inv.id}/mark-unpaid`);
      toast.success("Marked unpaid");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const currentEntities = entities[form.entity_type] || [];
  const filterEntities =
    filters.entity_type !== "all"
      ? entities[filters.entity_type] || []
      : [...(entities.client || []), ...(entities.partner || [])];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Billing</div>
          <h1 className="font-display font-semibold text-xl sm:text-2xl tracking-tight mt-1">Invoices</h1>
          <p className="text-sm text-ink-2 mt-1 tabular">
            {totals.count} invoice{totals.count === 1 ? "" : "s"} · Outstanding {" "}
            <span className="text-[color:var(--overdue)] font-semibold">{INR(totals.out)}</span> · Collected {" "}
            <span className="text-forest font-semibold">{INR(totals.col)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              onClick={exportCsv}
              variant="outline"
              disabled={exporting || invoices.length === 0}
              className="rounded-sm border-line bg-white text-ink hover:bg-[color:var(--bone)] hover:text-ink disabled:opacity-60"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1.5" />
              {exporting ? "Exporting…" : `Export ${totalCount} to CSV`}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="rounded-sm"
              data-testid="import-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setImportPaymentsOpen(true)}
              className="rounded-sm"
              data-testid="import-payments-btn"
            >
              <Check className="w-4 h-4 mr-1.5" /> Record Payments
            </Button>
          )}
          <Button
            onClick={openNew}
            className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
            data-testid="add-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New Invoice
          </Button>
        </div>
      </div>

      {/* Filters */}
      <InvoiceFiltersBar
        isAdmin={isAdmin}
        filters={filters}
        setFilters={setFilters}
        filterEntities={filterEntities}
      />

      {/* Table */}
      <div className="border border-line bg-card overflow-x-auto">
        {invoices.length === 0 ? (
          <div className="p-12 text-center text-ink-2 text-sm" data-testid="invoices-empty">
            No invoices match your filters.
          </div>
        ) : (
          <>
          <table className="w-full text-[13px] min-w-[980px]">
            <thead>
              <tr className="row-divider text-left">
                {isAdmin && (
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={invoices.length > 0 && selected.size === invoices.length}
                      onChange={toggleAll}
                      data-testid="select-all-invoices"
                      className="cursor-pointer"
                      title="Select all invoices matching the current filters (across all pages)"
                    />
                  </th>
                )}
                <th className="px-3 py-2.5 eyebrow">Invoice #</th>
                <th className="px-3 py-2.5 eyebrow">Entity</th>
                <th className="px-3 py-2.5 eyebrow">Invoice Date</th>
                <th className="px-3 py-2.5 eyebrow">Billing Period</th>
                <th className="px-3 py-2.5 eyebrow">Due</th>
                <th className="px-3 py-2.5 eyebrow">Due By</th>
                <th className="px-3 py-2.5 eyebrow text-right">
                  <button
                    type="button"
                    onClick={cycleAmountSort}
                    data-testid="sort-amount-header"
                    title={
                      sort?.by === "amount"
                        ? `Sorted by amount ${sort.order === "asc" ? "ascending" : "descending"} — click to ${sort.order === "desc" ? "sort ascending" : "clear"}`
                        : "Click to sort by amount (highest first)"
                    }
                    className="inline-flex items-center gap-1 eyebrow hover:text-[color:var(--aisensy-dark)] cursor-pointer select-none"
                  >
                    Amount
                    {sort?.by === "amount" ? (
                      sort.order === "asc" ? (
                        <ArrowUp className="w-3 h-3" />
                      ) : (
                        <ArrowDown className="w-3 h-3" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-40" />
                    )}
                  </button>
                </th>
                <th className="px-3 py-2.5 eyebrow">Status</th>
                <th className="px-3 py-2.5 eyebrow">Payment Ref</th>
                <th className="px-3 py-2.5 eyebrow text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="invoices-table">
              {paginatedInvoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="row-divider hover:bg-[color:var(--bone)] cursor-pointer"
                  onClick={() => openDetail(inv)}
                  data-testid={`invoice-row-${inv.id}`}
                >
                  {isAdmin && (
                    <td className="px-3 py-2.5 w-8" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(inv.id)}
                        onChange={() => toggleOne(inv.id)}
                        data-testid={`select-invoice-${inv.id}`}
                        className="cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2.5 tabular whitespace-nowrap">
                    <span>
                      {inv.invoice_number}
                    </span>
                    {inv.pdf_file_id && (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await downloadFile(inv.pdf_file_id, `${inv.invoice_number}.pdf`);
                          } catch (err) {
                            toast.error(formatApiError(err));
                          }
                        }}
                        title="Download PDF"
                        data-testid={`download-invoice-${inv.id}`}
                        className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-sm text-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-green-soft)] align-middle"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div>{inv.entity_name || "-"}</div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-2">
                      {inv.entity_type}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 tabular">{inv.invoice_date}</td>
                  <td className="px-3 py-2.5 tabular text-ink-2">
                    {fmtDMY(inv.billing_period_start)} → {fmtDMY(inv.billing_period_end)}
                  </td>
                  <td className="px-3 py-2.5 tabular">{inv.due_date}</td>
                  <td className="px-3 py-2.5 tabular">
                    <DueByPill invoice={inv} />
                  </td>
                  <td className="px-3 py-2.5 tabular text-right">{INR(inv.amount)}</td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={inv.status} isOverdue={inv.is_overdue} />
                  </td>
                  <td className="px-3 py-2.5 tabular text-ink-2">
                    {inv.status === "paid" ? (
                      <>
                        {inv.utr_number && <div>UTR: {inv.utr_number}</div>}
                        {inv.payment_id && <div>PayID: {inv.payment_id}</div>}
                        {inv.payment_date && <div>on {inv.payment_date}</div>}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {inv.status === "paid" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markUnpaid(inv)}
                        className="rounded-sm h-7 px-2"
                        data-testid={`mark-unpaid-${inv.id}`}
                      >
                        <Undo2 className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => openPay(inv)}
                        className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white h-7 px-2"
                        data-testid={`mark-paid-${inv.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> {inv.status === "partial" ? "Add" : "Mark Paid"}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(inv)}
                      className="rounded-sm h-7 px-2 ml-1"
                      data-testid={`edit-invoice-${inv.id}`}
                    >
                      Edit
                    </Button>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(inv)}
                        className="rounded-sm h-7 px-2 text-[color:var(--overdue)]"
                        data-testid={`delete-invoice-${inv.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            total={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            entityLabel="invoices"
          />
          </>
        )}
      </div>

      {/* Invoice form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit invoice" : "New invoice"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isAdmin && (
              <div>
                <Label className="eyebrow">Business Unit</Label>
                <Select
                  value={form.entity_type}
                  onValueChange={(v) => setForm({ ...form, entity_type: v, entity_id: "" })}
                >
                  <SelectTrigger className="rounded-sm mt-1" data-testid="form-entity-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {canSeeClient && <SelectItem value="client">Client</SelectItem>}
                    {canSeePartner && <SelectItem value="partner">Partner</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={isAdmin ? "" : "sm:col-span-2"}>
              <Label className="eyebrow">Entity <span className="text-[color:var(--overdue)]">*</span></Label>
              <Select
                value={form.entity_id}
                onValueChange={(v) => {
                  const ent = currentEntities.find((x) => x.id === v);
                  const tl = ent?.payment_timeline_days;
                  let nextDue = form.due_date;
                  if (form.invoice_date && tl) {
                    const d = new Date(form.invoice_date + "T00:00:00");
                    d.setDate(d.getDate() + Number(tl));
                    nextDue = d.toISOString().slice(0, 10);
                  }
                  setForm({ ...form, entity_id: v, due_date: nextDue });
                }}
              >
                <SelectTrigger className="rounded-sm mt-1" data-testid="form-entity">
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  {currentEntities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Invoice Number <span className="text-[color:var(--overdue)]">*</span></Label>
              <Input
                className="rounded-sm mt-1"
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                data-testid="form-invoice-number"
                required
              />
            </div>
            <div>
              <Label className="eyebrow">Amount (₹) <span className="text-[color:var(--overdue)]">*</span></Label>
              <Input
                type="number"
                className="rounded-sm mt-1 tabular"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="form-amount"
                required
              />
            </div>
            <div>
              <Label className="eyebrow">Invoice Date <span className="text-[color:var(--overdue)]">*</span></Label>
              <DatePicker
                testId="form-invoice-date"
                value={form.invoice_date}
                onChange={(invDate) => {
                  const ent = currentEntities.find((x) => x.id === form.entity_id);
                  const timeline = ent?.payment_timeline_days;
                  let nextDue = form.due_date;
                  if (invDate && timeline) {
                    const d = new Date(invDate + "T00:00:00");
                    d.setDate(d.getDate() + Number(timeline));
                    nextDue = d.toISOString().slice(0, 10);
                  }
                  setForm({ ...form, invoice_date: invDate, due_date: nextDue });
                }}
                placeholder="Select invoice date"
              />
            </div>
            <div>
              <Label className="eyebrow">Due Date <span className="text-[color:var(--overdue)]">*</span></Label>
              <DatePicker
                testId="form-due-date"
                value={form.due_date}
                onChange={(v) => setForm({ ...form, due_date: v })}
                placeholder="Select due date"
              />
              {(() => {
                const ent = currentEntities.find((x) => x.id === form.entity_id);
                return ent?.payment_timeline_days ? (
                  <div className="text-[10px] text-ink-2 mt-1">
                    Auto-set to invoice date + {ent.payment_timeline_days} days (this entity&apos;s payment timeline). You can override.
                  </div>
                ) : null;
              })()}
            </div>
            <div>
              <Label className="eyebrow">Billing Period Start <span className="text-[color:var(--overdue)]">*</span></Label>
              <DatePicker
                value={form.billing_period_start}
                onChange={(v) => setForm({ ...form, billing_period_start: v })}
                placeholder="Period start"
              />
            </div>
            <div>
              <Label className="eyebrow">Billing Period End <span className="text-[color:var(--overdue)]">*</span></Label>
              <DatePicker
                value={form.billing_period_end}
                onChange={(v) => setForm({ ...form, billing_period_end: v })}
                placeholder="Period end"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="eyebrow">Notes</Label>
              <Textarea
                className="rounded-sm mt-1"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="eyebrow">Invoice PDF <span className="text-ink-2 normal-case font-normal ml-1">(optional)</span></Label>
              <div className="mt-1 border border-dashed border-line rounded-sm p-4 flex items-center justify-between">
                {form.pdf_file_id ? (
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-forest" />
                    <span className="font-semibold truncate max-w-[240px]">
                      {form.pdf_filename || "invoice.pdf"}
                    </span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, pdf_file_id: null, pdf_filename: null })}
                      className="text-ink-2 hover:text-[color:var(--overdue)]"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-ink-2">No file. Attach an invoice PDF (max 20MB)</span>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => onPdf(e.target.files?.[0])}
                    data-testid="pdf-upload-input"
                  />
                    <span className="inline-flex items-center px-3 py-1.5 bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white text-xs font-semibold rounded-sm">
                      <Upload className="w-3.5 h-3.5 mr-1.5" />
                      {(() => {
                        if (uploading) return "Uploading…";
                        return form.pdf_file_id ? "Replace" : "Upload PDF";
                      })()}
                    </span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={save}
              className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
              data-testid="invoice-save-btn"
            >
              Save Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid dialog */}
      <MarkPaidDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        target={payTarget}
        form={payForm}
        setForm={setPayForm}
        onSubmit={submitPay}
      />

      {/* Invoice Detail dialog */}
      <InvoiceDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        detail={detail}
        detailLoading={detailLoading}
        sortedPayments={sortedDetailPayments}
        onEdit={openEdit}
        onMarkPaid={openPay}
      />
      {isAdmin && (
        <BulkBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onDelete={bulkDelete}
          onDuplicate={bulkDuplicate}
          entityLabel="invoice"
        />
      )}
      <DuplicateInvoiceDialog
        open={dupOpen}
        onOpenChange={(v) => {
          setDupOpen(v);
          if (!v) setDupSource(null);
        }}
        source={dupSource}
        onSubmit={submitDuplicateOverride}
      />
      <ImportCSVDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Import Invoices"
        sampleUrl="/invoices/sample-csv"
        sampleFilename="sample_invoices.csv"
        importUrl="/invoices/import"
        requiredColumns={INVOICE_IMPORT_COLUMNS}
        entityLabel="invoice"
        previewCountKey="would_create"
        previewColumns={INVOICE_IMPORT_PREVIEW_COLUMNS}
        onDone={() => { load(); }}
      />
      <ImportCSVDialog
        open={importPaymentsOpen}
        onOpenChange={setImportPaymentsOpen}
        title="Record Payments in Bulk"
        sampleUrl="/invoices/sample-payments-csv"
        sampleFilename="sample_record_payments.csv"
        importUrl="/invoices/import-payments"
        requiredColumns={PAYMENT_IMPORT_COLUMNS}
        entityLabel="payment"
        resultKey="recorded"
        previewCountKey="would_record"
        previewColumns={PAYMENT_IMPORT_PREVIEW_COLUMNS}
        onDone={() => { load(); }}
      />
    </div>
  );
}
